"use client";

import { NextImage } from "@components/NextImage";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import type { SaveGoalArgs } from "./types";
import { cn } from "utils";
import { ospThemes } from "@ospweb/sdk-ui";
import { GoalCardActivateHeader } from "@components/goal";
import { GOAL_CARD_ACTIVATE_BG_SRC } from "@screens/plan/constants/goalCardActivateVisual";

/** @deprecated Import from `@/src/screens/plan/constants/goalCardActivateVisual` */

export type SaveGoalCardGoalPreviewProps = {
  args: Pick<SaveGoalArgs, "title" | "summary" | "memoryHitNote">;
  className?: string;
  /** `activate` — UI14 single-line goal (96138:332439); `legacy` — dual title/summary */
  layout?: "legacy" | "activate";
};

export type SaveGoalCardGoalSectionProps = SaveGoalCardGoalPreviewProps;

/**
 * Goal Card V3 内容区 — SaveGoalCard 与 Aggregated ⑧ 共用。
 * Figma 89002:38623 / 96138:332439 (activate).
 */
export const SaveGoalCardGoalSection: React.FC<
  SaveGoalCardGoalSectionProps
> = ({ args, className, layout = "legacy" }) => {
  const goalStatement = args.summary?.trim() || args.title?.trim() || "";

  if (layout === "activate") {
    return (
      <div
        className={cn(
          "relative z-[1] flex min-h-[164px] flex-col overflow-hidden",
          className,
        )}
        data-testid="save-goal-card-goal-section"
        data-layout="activate"
      >
        <GoalCardActivateHeader
          statement={goalStatement}
          className="flex min-h-[164px] flex-col px-5 pb-[22px] pt-5"
          bgTestId="save-goal-card-bg"
          statementTestId="save-goal-card-summary"
        />
      </div>
    );
  }

  const summaryText = args.summary?.trim() || args.title;
  const title = args.title?.trim() || args.summary;

  return (
    <div
      className={cn(
        "relative z-[1] flex flex-col gap-5 overflow-hidden p-5 pt-3",
        className,
      )}
      data-testid="save-goal-card-goal-section"
      data-layout="legacy"
    >
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden
        data-testid="save-goal-card-bg"
      >
        <NextImage
          src={GOAL_CARD_ACTIVATE_BG_SRC}
          alt=""
          width={360}
          height={200}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="relative z-[1] flex flex-col gap-3">
        <div
          className="flex w-fit items-center gap-2 rounded-xl"
          data-testid="save-goal-card-meta"
        >
          <OspIcons
            name="goal"
            size={21}
            className="text-content_inverse_primary"
            color={ospThemes.colors.content_inverse_primary}
          />
        </div>
        <p
          className="headline_regular_s m-0 text-content_inverse_primary"
          data-testid="save-goal-card-summary"
        >
          {title}
        </p>
        <p className="body_s text-content_inverse_primary">{summaryText}</p>
        {args.memoryHitNote ? (
          <p className="body_s m-0 text-content_inverse_primary/80">
            {args.memoryHitNote}
          </p>
        ) : null}
      </div>
    </div>
  );
};

/** 仅 Goal 预览（无 CTA）— 宽度由 `className` 控制 */
export const SaveGoalCardGoalPreview: React.FC<
  SaveGoalCardGoalPreviewProps
> = ({ args, className, layout = "legacy" }) => (
  <div
    className={cn(
      "relative flex w-full flex-col overflow-hidden rounded-2xl border border-border_accent5_transparent50 bg-background_solid_neutral_primary",
      className,
    )}
    data-testid="save-goal-card-goal-preview"
  >
    <SaveGoalCardGoalSection args={args} layout={layout} />
  </div>
);
